import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { Users, FileText, Loader2, User, CheckCircle2 } from "lucide-react";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { PageHeader } from "@shared/components/PageHeader";
import { normalizeDate } from "@shared/utils/dateUtils";
import { useUtilityServices, useOwnerType, useCity, useSearchMasterConsumers } from "../../../../../hooks";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import { useTranslation } from "react-i18next";

const MasterAccountSearch = ({ form }: { form: any }) => {
  const { remoteUtilityId } = getLoginDataFromStorage();
  const [masterSearchTerm, setMasterSearchTerm] = useState(form.getValues("businessAccountNumber") || "");
  const [masterDetails, setMasterDetails] = useState<any>(null);
  const { t } = useTranslation();

  // Sync state if form changes dynamically (e.g. on reset)
  useEffect(() => {
    const subscription = form.watch((value: any, { name }: any) => {
      if (!name || name === "businessAccountNumber") {
        setMasterSearchTerm(value.businessAccountNumber || "");
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const { isFetching: isMasterSearching, refetch: refetchMasterSearch } = useSearchMasterConsumers({
    remote_utility_id: remoteUtilityId?.toString() || '',
    search_data: masterSearchTerm,
  });

  const handleVerifyMaster = useCallback(async () => {
    if (masterSearchTerm.length < 3) return;

    const { data, isError } = await refetchMasterSearch();

    if (isError || !data?.result || Object.keys(data.result).length === 0) {
      // Clear previously set values on failure
      form.setValue("businessAccountName", "", { shouldValidate: true, shouldDirty: true });
      form.setValue("businessAccountId", "", { shouldValidate: true, shouldDirty: true });
      return;
    }

    const result = Array.isArray(data.result) ? data.result[0] : data.result;
    if (!result) {
      form.setValue("businessAccountName", "", { shouldValidate: true, shouldDirty: true });
      form.setValue("businessAccountId", "", { shouldValidate: true, shouldDirty: true });
      setMasterDetails(null);
      return;
    }

    const fullName = `${result.firstName || ''} ${result.lastName || ''}`.trim() || t("Unknown Consumer");
    const accountId = result.id?.toString() || '';

    form.setValue("businessAccountNumber", result.consumerNo || masterSearchTerm, { shouldValidate: true, shouldDirty: true });
    form.setValue("businessAccountName", fullName, { shouldValidate: true, shouldDirty: true });
    form.setValue("businessAccountId", accountId, { shouldValidate: true, shouldDirty: true });
    setMasterSearchTerm(result.consumerNo || masterSearchTerm);
    setMasterDetails(result);
  }, [masterSearchTerm, refetchMasterSearch, form, t]);

  const isMasterSelected = !!form.watch("businessAccountId");

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
      {!isMasterSelected ? (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">
              {t("Master Account Search")}
            </h3>
          </div>

          <div className="relative">
            <div className="flex gap-2">
              <Input
                placeholder={t("Enter Master Account Number")}
                value={masterSearchTerm}
                onChange={(e: any) => {
                  setMasterSearchTerm(e.target.value);
                  form.setValue("businessAccountNumber", e.target.value, { shouldValidate: true, shouldDirty: true });
                }}
                className="w-full"
              />
              <Button
                type="button"
                onClick={handleVerifyMaster}
                disabled={isMasterSearching || masterSearchTerm.length < 3}
              >
                {isMasterSearching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {t("Verify")}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {t("Enter exact master account number to link")}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200 shadow-sm animate-in fade-in zoom-in duration-300">
          <div className="flex items-center gap-2 mb-3">
            <User className="h-4 w-4 text-green-600" />
            <h4 className="font-medium text-green-800">
              {t("Selected Master Account")}
            </h4>
            <button
              type="button"
              onClick={() => {
                form.setValue("businessAccountNumber", "", { shouldValidate: true, shouldDirty: true });
                form.setValue("businessAccountName", "", { shouldValidate: true, shouldDirty: true });
                form.setValue("businessAccountId", "", { shouldValidate: true, shouldDirty: true });
                setMasterSearchTerm("");
                setMasterDetails(null);
              }}
              className="ml-auto text-[10px] font-black text-blue-600 hover:text-blue-800 transition-colors tracking-widest bg-white px-3 py-1 rounded-lg border border-blue-200 shadow-sm hover:shadow-md active:scale-95"
            >
              {t("Change")}
            </button>
          </div>
          <div className="flex items-center gap-3 pb-4 mb-4 border-b border-green-200">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">{form.watch("businessAccountName")}</p>
              <p className="text-xs text-gray-500">{form.watch("businessAccountNumber")}</p>
            </div>
          </div>
          
          {masterDetails && (
            <div className="grid grid-cols-2 gap-y-4 gap-x-4 text-xs">
              <div>
                <p className="text-gray-400 font-semibold mb-1  tracking-wider text-[10px]">{t("Category")}</p>
                <p className="text-gray-800 font-medium">{masterDetails.categoryDisplay || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-400 font-semibold mb-1  tracking-wider text-[10px]">{t("Sub Category")}</p>
                <p className="text-gray-800 font-medium">{masterDetails.subCategoryDisplay || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-400 font-semibold mb-1  tracking-wider text-[10px]">{t("Email")}</p>
                <p className="text-gray-800 font-medium truncate" title={masterDetails.email}>{masterDetails.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-400 font-semibold mb-1  tracking-wider text-[10px]">{t("Phone")}</p>
                <p className="text-gray-800 font-medium">{masterDetails.contactNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-400 font-semibold mb-1 tracking-wider text-[10px]">{t("Status")}</p>
                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wider">{masterDetails.statusDisplay || 'ACTIVE'}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ServiceDetailProps {
  onDataChange?: (data: any) => void;
  onValidationChange?: (isValid: boolean) => void;
  consumerData?: any;
  onFormReady?: (form: any) => void;
}

export function ServiceDetail({ 
  onDataChange, 
  onValidationChange,
  consumerData,
  onFormReady
}: ServiceDetailProps) {
  const { remoteUtilityId } = getLoginDataFromStorage();
  const { t } = useTranslation();
  
  const { data: utilityData } = useUtilityServices({ remote_utility_id: remoteUtilityId });
  const { data: cityData } = useCity({ remote_utility_id: remoteUtilityId });
  const { data: ownerTypeData } = useOwnerType();

  // Add a ref to track if we're currently updating fields programmatically
  const isUpdatingRef = useRef(false);
const ownerTypeOptions = useMemo(
  () =>
    ownerTypeData?.result?.map((ownerType) => ({
      value: ownerType.key,
      label: ownerType.value,
      className: "",
    })) || [],
  [ownerTypeData]
);
  const cityOptions = useMemo(
    () =>
      cityData?.result?.map((city) => ({
        value: city.code,
        label: city.name,
        className: "",
      })) || [],
    [cityData]
  );

  const getUtilityOptions = useMemo(
    () =>
      utilityData
        ? utilityData
            .filter((utility) => utility.isActive === true)
            .map((utility) => ({
              value: utility.name,
              label: utility.name,
              className: "",
            }))
        : [],
    [utilityData]
  );

  const formFields: FormField[] = useMemo(
    () => [
      // ==================== SERVICE DETAILS SECTION ====================
      {
        name: "serviceStartDate",
        label: t("Desired Service Start Date"),
        type: "datepicker",
        required: true,
        placeholder: "dd-mm-yyyy",
        helperText: t("Select preferred start date"),
        // group: "Service Details",
        className: "col-span-4",
      },
      {
        name: "preferredContactMethod",
        label: t("Preferred Contact Method"),
        type: "multi-select",
        options: [
          { label: t("Email"), value: "Email", className: "" },
          { label: t("Phone"), value: "Phone", className: "" },
        ],
        required: true,
        helperText: t("Select how you would like to be contacted"),
        // group: "Service Details",
        className: "col-span-4",
      },
      {
        name: "selectServicesHeader",
        label: t("Select Utility Services"),
        type: "multi-select",
       required: true,
        // group: "Service Details",
        options: getUtilityOptions,
        groupColorNeeded: false,
        className: "col-span-4",
      },
      // ==================== PROPERTY TYPE SECTION ====================
{
  name: "propertyType",
  label: t("Property Structure"),
  type: "radio-card",
  required: true,
  helperText: t("Select property ownership type"),

  className: "col-span-12",
  options: [
    {
      value: "individual",
      label: t("Individual Property Owner"),
      description: t("Own and occupy the property"),
      className: "",
    },
    {
      value: "tenant",
      label: t("Tenant / Renter Account"),
      description: t("Renting the property from an owner"),
      className: "",
    },
    {
      value: "business",
      label: t("Business Account"),
      description: t("Commercial or business account structure"),
      className: "",
    },
  ],

},
// ==================== OWNER DETAILS SECTION (conditional) ====================
{
  name: "ownerName",
  label: t("Property Owner Name"),
  type: "text",
  required: true,
  placeholder: t("Enter property owner name"),
  group: t("Property Owner Details"),
  groupHelperText: t("Enter landlord or property owner details"),
  groupColorNeeded: false,
  className: "col-span-6",
  showWhen: (formValues) => formValues.propertyType === "tenant",
  groupBorderNeeded: false,
},
{
  name: "ownerPhone",
  label: t("Property Owner Phone"),
  type: "country-phone",
  required: true,
  placeholder: "+1 (555) 000-0000",
  group: t("Property Owner Details"),
  groupColorNeeded: false,
  className: "col-span-6",
  showWhen: (formValues) => formValues.propertyType === "tenant",
  groupBorderNeeded: false,
},
{
  name: "ownerEmail",
  label: t("Property Owner Email"),
  type: "email",
  required: false,
  placeholder: "owner@example.com",
  group: t("Property Owner Details"),
  groupColorNeeded: false,
  className: "col-span-6",
  showWhen: (formValues) => formValues.propertyType === "tenant",
  groupBorderNeeded: false,
},
{
  name: "ownerAddress",
  label: t("Property Owner Address"),
  type: "text",
  required: true,
  placeholder: t("Enter property owner address"),
  group: t("Property Owner Details"),
  groupColorNeeded: false,
  className: "col-span-12",
  showWhen: (formValues) => formValues.propertyType === "tenant",
  groupBorderNeeded: false,
},
// ==================== BUSINESS ACCOUNT SECTION (conditional) ====================
{
  name: "businessAccountLink",
  label: t("Link Business Account"),
  type: "radio-card",
  required: true,
  className: "col-span-12",
  showWhen: (formValues) => formValues.propertyType === "business",
  options: [
    {
      value: "new",
      label: t("New Master Business Account"),
      description: t("Create a new master business account"),
      className: "",
    },
    {
      value: "existing",
      label: t("Sub-Account to Existing Master Account"),
      description: t("Link to an existing master business account"),
      className: "",
    },
  ],
},
// ==================== EXISTING BUSINESS ACCOUNT DETAILS (conditional) ====================
{
  name: "businessAccountNumber",
  label: t("Master Account Number"),
  type: "custom",
  required: true,
  customRender: (field, form, error) => <MasterAccountSearch form={form} />,

  placeholder: t("Enter account number"),
  helperText: t("Enter your existing master business account number"),
  group: t("Master Account Details"),
  groupHelperText: t("Verified master account details"),
  groupColorNeeded: false,
  className: "col-span-12",
  showWhen: (formValues) =>
    formValues.propertyType === "business" &&
    formValues.businessAccountLink === "existing",
},
{
  name: "businessAccountId",
  type: "hidden",
},
      // ==================== SERVICE ADDRESS SECTION ====================
      {
        name: "serviceHouseUnit",
        label: t("House / Unit / Apt No."),
        type: "text",
        required: true,
        placeholder: "e.g. 123, Apt 4B",
        helperText: t("Building number or apartment/suite"),
        group: t("Service Address Details"),
        className: "col-span-4",
        groupColorNeeded: false,
        groupBorderNeeded: false,
      },
      {
        name: "serviceZipCode",
        label: t("ZIP / Postal Code"),
        type: "text",
        required: false,
        placeholder: "e.g. 12345",
        helperText: t("Postal code of the service location"),
        group: t("Service Address Details"),
        className: "col-span-4",
          groupBorderNeeded: false,
      },
      {
        name: "serviceCity",
        label: t("City"),
        type: "select",
        required: true,
        options: cityOptions,
        placeholder: t("Select city"),
        helperText: t("City of the service location"),
        group: t("Service Address Details"),
        className: "col-span-4",
          groupBorderNeeded: false,
      },
      {
        name: "serviceStreetAddress",
        label: t("Street Address"),
        type: "text",
        required: true,
        placeholder: "e.g. 123 Main Street",
        helperText: t("Full street address"),
        group: t("Service Address Details"),
        className: "col-span-12",
        groupBorderNeeded: false,
      },

      // ==================== MAILING ADDRESS SECTION ====================
      {
        name: "sameAsServiceAddress",
        label: t("Mailing address same as service address"),
        type: "checkbox",
        required: false,
        helperText: t("Check if mailing address matches service address"),
        group: t("Mailing / Billing Address"),
        className: "col-span-6",
        groupColorNeeded: false,
        groupBorderNeeded: false,
      },
      {
        name: "paperlessBilling",
        label: t("Paperless Billing"),
        type: "checkbox",
        required: false,
        helperText: t("Receive bills digitally via email"),
        group: t("Mailing / Billing Address"),
        className: "col-span-6",
        groupColorNeeded: false,
        groupBorderNeeded: false,
      },
      {
        name: "mailingHouseUnit",
        label: t("House / Unit / Apt No."),
        type: "text",
        required: false,
        placeholder: "e.g. 123, Apt 4B",
        helperText: t("Building number or apartment/suite"),
        group: t("Mailing / Billing Address"),
        showWhen: (formValues) => formValues.sameAsServiceAddress !== true,
        className: "col-span-4",
        groupBorderNeeded: false,
      },
      {
        name: "mailingZipCode",
        label: t("ZIP / Postal Code"),
        type: "text",
        required: false,
        placeholder: "e.g. 12345",
        helperText: t("Postal code of the service location"),
        group: t("Mailing / Billing Address"),
        showWhen: (formValues) => formValues.sameAsServiceAddress !== true,
        className: "col-span-4",
        groupBorderNeeded: false,
      },
      {
        name: "mailingCity",
        label: t("City"),
        type: "select",
        required: false,
        options: cityOptions,
        placeholder: t("Select city"),
        helperText: t("City of the service location"),
        group: t("Mailing / Billing Address"),
        showWhen: (formValues) => formValues.sameAsServiceAddress !== true,
        className: "col-span-4",
        groupBorderNeeded: false,
      },
      {
        name: "mailingStreetAddress",
        label: t("Street Address"),
        type: "text",
        required: false,
        placeholder: "e.g. 123 Main Street",
        helperText: t("Full street address"),
        group: t("Mailing / Billing Address"),
        className: "col-span-12",
        showWhen: (formValues) => formValues.sameAsServiceAddress !== true,
        groupBorderNeeded: false,
      },
    ],
    [cityOptions, getUtilityOptions, t]
  );

const defaultFormValues = useMemo(() => {
  if (!consumerData?.result) return {};

  
  const data = consumerData.result;
  // Use addressData which has the correct structure with UPPERCASE keys
  const serviceAddress = data.addressData?.service;
  const billingAddress = data.addressData?.billing;

  // Check if billing address is same as service address
  // If billing address is empty OR identical to service address, it means same as service
  const isSameAddress = 
    (!billingAddress?.UNIT && !billingAddress?.ADDRESS && !billingAddress?.CITY && !billingAddress?.ZIPCODE) ||
    (billingAddress?.UNIT === serviceAddress?.UNIT && 
     billingAddress?.ADDRESS === serviceAddress?.ADDRESS && 
     billingAddress?.CITY === serviceAddress?.CITY && 
     billingAddress?.ZIPCODE === serviceAddress?.ZIPCODE);

  // Extract ownership details configuration
  const ownershipDetails = data.ownershipDetails || {};
  let propertyType = "";
  let businessAccountLink = "";
  
  if (ownershipDetails.structureChoice === "INDIVIDUAL") {
    propertyType = "individual";
  } else if (ownershipDetails.structureChoice === "TENANT" || ownershipDetails.structureChoice === "Tenant") {
    propertyType = "tenant";
  } else if (ownershipDetails.structureChoice === "BUSINESS_NEW_MASTER" || ownershipDetails.structureChoice === "Master") {
    propertyType = "business";
    businessAccountLink = "new";
  } else if (ownershipDetails.structureChoice === "BUSINESS_LINK_EXISTING" || ownershipDetails.structureChoice === "Sub_account") {
    propertyType = "business";
    businessAccountLink = "existing";
  }

  const tenantDetail = ownershipDetails.ownershipDetail || {};

  return {
    serviceStartDate: normalizeDate(data.registrationDate) || "",
    preferredContactMethod: data.preferredContactMethod || [],
    selectServicesHeader: data.utilityService || [],
    
    propertyType,
    ownerName: tenantDetail.ownerName || "",
    ownerPhone: tenantDetail.ownerPhone || "",
    ownerEmail: tenantDetail.ownerEmail || "",
    ownerType: tenantDetail.ownerType || "",
    ownerAddress: tenantDetail.ownerAddress || "",
    
    businessAccountLink,
    businessAccountNumber: ownershipDetails.masterAccountNo || ownershipDetails.appliedMasterAccountNo || "",
    businessAccountName: ownershipDetails.masterAccountName || ownershipDetails.appliedMasterAccountEmail || "",
    
    // Service Address - from addressData.service
    serviceHouseUnit: serviceAddress?.UNIT || "",
    serviceZipCode: serviceAddress?.ZIPCODE || "",
    serviceCity: serviceAddress?.CITY || "",
    serviceStreetAddress: serviceAddress?.ADDRESS || "",
    
    sameAsServiceAddress: isSameAddress,
    paperlessBilling: data.isEBill || false,
    
    // Mailing Address - from addressData.billing (only if different)
    mailingHouseUnit: isSameAddress ? "" : (billingAddress?.UNIT || ""),
    mailingZipCode: isSameAddress ? "" : (billingAddress?.ZIPCODE || ""),
    mailingCity: isSameAddress ? "" : (billingAddress?.CITY || ""),
    mailingStreetAddress: isSameAddress ? "" : (billingAddress?.ADDRESS || ""),
  };
}, [consumerData]);

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  useEffect(() => {
    if (onFormReady) {
      onFormReady(form);
    }
  }, [form, onFormReady]);

  // Reset form and populate data when consumer data changes
  useEffect(() => {
    if (!consumerData?.result) return;

    // allow fields to mount based on showWhen
    setTimeout(() => {
      FormService.populateForm(form, defaultFormValues, formFields);
      const currentValues = form.getValues();
      if (onDataChange) {
        onDataChange(buildPayload(currentValues));
      }
    }, 0);
  }, [consumerData, defaultFormValues, form]);

  const buildPayload = (formValues: any) => {
      const getCityLabel = (cityCode: string) => {
    return cityOptions.find(option => option.value === cityCode)?.label || "";
  };
    const payload: any = {
      service_start_date: formValues.serviceStartDate || "",
      preferred_contact_methods: formValues.preferredContactMethod || [],
      selected_services: formValues.selectServicesHeader || [],
      property_type: formValues.propertyType || "",
      service_address: {
        house_unit_number: formValues.serviceHouseUnit || "",
        zip_postal_code: formValues.serviceZipCode || "",
        city: formValues.serviceCity || "",
         city_display: getCityLabel(formValues.serviceCity),
        street_address: formValues.serviceStreetAddress || "",
      },
      same_as_service_address: formValues.sameAsServiceAddress || false,
      paperless_billing: formValues.paperlessBilling || false,
      ownerDetails: {
        name: formValues.ownerName || "",
        phone: formValues.ownerPhone || "",
        email: formValues.ownerEmail || null,
        type: formValues.ownerType || "",
        address: formValues.ownerAddress || "",
      },
      business_account_link: formValues.businessAccountLink || "",
      existing_business_account: {
        account_number: formValues.businessAccountNumber || "",
        account_email: formValues.businessAccountName || "",
        account_id: formValues.businessAccountId || "",
      },
    };

    // If "Same as service address" is checked, copy service address to mailing address
    if (formValues.sameAsServiceAddress) {
      payload.mailing_address = {
        house_unit_number: formValues.serviceHouseUnit || "",
        zip_postal_code: formValues.serviceZipCode || "",
        city: formValues.serviceCity || "",
         city_display: getCityLabel(formValues.serviceCity),
        street_address: formValues.serviceStreetAddress || "",
      };
    } else {
      // Otherwise use the separate mailing address fields
      payload.mailing_address = {
        house_unit_number: formValues.mailingHouseUnit || "",
        zip_postal_code: formValues.mailingZipCode || "",
        city: formValues.mailingCity || "",
         city_display: getCityLabel(formValues.mailingCity),
        street_address: formValues.mailingStreetAddress || "",
      };
    }

    return payload;
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    // Prevent recursive calls
    if (isUpdatingRef.current) {
      return;
    }

    try {
      isUpdatingRef.current = true;

      // When "Same as service address" is checked, copy service address to mailing address
      if (fieldName === "sameAsServiceAddress" && value === true) {
        const serviceHouseUnit = form.getValues("serviceHouseUnit");
        const serviceZipCode = form.getValues("serviceZipCode");
        const serviceCity = form.getValues("serviceCity");
        const serviceStreetAddress = form.getValues("serviceStreetAddress");

        form.setValue("mailingHouseUnit", serviceHouseUnit, { shouldValidate: false, shouldDirty: false });
        form.setValue("mailingZipCode", serviceZipCode, { shouldValidate: false, shouldDirty: false });
        form.setValue("mailingCity", serviceCity, { shouldValidate: false, shouldDirty: false });
        form.setValue("mailingStreetAddress", serviceStreetAddress, { shouldValidate: false, shouldDirty: false });
      }

      // When "Same as service address" is unchecked, clear mailing address fields
      if (fieldName === "sameAsServiceAddress" && value === false) {
        form.setValue("mailingHouseUnit", "", { shouldValidate: false, shouldDirty: false });
        form.setValue("mailingZipCode", "", { shouldValidate: false, shouldDirty: false });
        form.setValue("mailingCity", "", { shouldValidate: false, shouldDirty: false });
        form.setValue("mailingStreetAddress", "", { shouldValidate: false, shouldDirty: false });
      }

      // When service address fields change and "Same as service address" is checked,
      // update the mailing address fields as well
      const serviceAddressFields = [
        "serviceHouseUnit",
        "serviceZipCode",
        "serviceCity",
        "serviceStreetAddress",
      ];

      if (
        serviceAddressFields.includes(fieldName) &&
        form.getValues("sameAsServiceAddress") === true
      ) {
        const mailingFieldMap: Record<string, string> = {
          serviceHouseUnit: "mailingHouseUnit",
          serviceZipCode: "mailingZipCode",
          serviceCity: "mailingCity",
          serviceStreetAddress: "mailingStreetAddress",
        };

        const mailingField = mailingFieldMap[fieldName];
        if (mailingField) {
          form.setValue(mailingField as any, value, { shouldValidate: false, shouldDirty: false });
        }
      }

      if (onDataChange) {
        const currentValues = form.getValues();
        onDataChange(buildPayload(currentValues));
      }
    } finally {
      // Always reset the flag in finally block
      isUpdatingRef.current = false;
    }
  };

  // Watch form validation state
  useEffect(() => {
    if (onValidationChange) {
      const subscription = form.watch(() => {
        const errors = form.formState.errors;
        const isValid = Object.keys(errors).length === 0;
        onValidationChange(isValid);
      });
      return () => subscription.unsubscribe();
    }
  }, [form, onValidationChange]);



  const getServiceCityLabel = () => {
  const cityValue = form.watch("serviceCity");
  return cityOptions.find(option => option.value === cityValue)?.label || "";
};

const getMailingCityLabel = () => {
  const cityValue = form.watch("mailingCity");
  return cityOptions.find(option => option.value === cityValue)?.label || "";
};

  return (
    <div className="space-y-6">
        <div className="mb-4 flex items-center gap-4">
              <div className="flex flex-col">
                <PageHeader
                  title={t("Service Details")}
                  subtitle={t("Provide your service address, preferred services, and billing preferences")}
                />
              </div>
            </div>
      {/* Form Section */}
      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={() => {}}
        onFieldChange={handleFieldChange}
        gridColumns={12}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          classes: FIELD_CLASSES,
        }}
      />

      <div className="space-y-4 pt-6 border-t">
        <h2 className="text-l font-semibold text-gray-900">{t("Address Preview")}</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Service Address Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-blue-900">
                {t("Service Address")}
              </h3>
            </div>
            <p className="text-sm text-blue-700 mb-3">
              {t("How your service address will appear on bills and account records")}
            </p>
            <div className="space-y-1 text-sm text-blue-900">
              <p className="font-medium">
                {form.watch("serviceHouseUnit") || ""}
              </p>
              <p>{form.watch("serviceStreetAddress") || ""}</p>
             <p>
  {getServiceCityLabel()}
  {getServiceCityLabel() && form.watch("serviceZipCode") && ", "}
  {form.watch("serviceZipCode") || ""}
</p>
            </div>
          </div>

          {/* Mailing Address Preview */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-green-900">
                {t("Mailing / Billing Address")}
              </h3>
            </div>
            <p className="text-sm text-green-700 mb-3">
              {t("Where paper bills and official mail will be delivered")}
            </p>
            <div className="space-y-1 text-sm text-green-900">
              {form.watch("sameAsServiceAddress") ? (
                <>
                  <p className="font-medium">
                    {form.watch("serviceHouseUnit") || ""}
                  </p>
                  <p>{form.watch("serviceStreetAddress") || ""}</p>
               <p>
  {getServiceCityLabel()}
  {getServiceCityLabel() && form.watch("serviceZipCode") && ", "}
  {form.watch("serviceZipCode") || ""}
</p>
                </>
              ) : (
                <>
                  <p className="font-medium">
                    {form.watch("mailingHouseUnit") || ""}
                  </p>
                  <p>{form.watch("mailingStreetAddress") || ""}</p>
                 <p>
  {getMailingCityLabel()}
  {getMailingCityLabel() && form.watch("mailingZipCode") && ", "}
  {form.watch("mailingZipCode") || ""}
</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}